import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { z } from "zod";
import axios from "axios";
import { useAuth } from "@clerk/clerk-react";

const EventType = z.enum(["LEVEL_UP", "NEW_CONTENT", "NOTIFICATION", "POINT_REWARD"]);

const globalEventReadSchema = z.object({
    id: z.string(),
    userId: z.string(),
    globalEventId: z.string(),
    readAt: z.string(),
});

export const globalEventSchema = z.object({
    id: z.string(),
    name: z.string(),
    description: z.string(),
    type: EventType,
    link: z.string().optional(),
    archived: z.boolean(),
    createdAt: z.string(),
    updatedAt: z.string(),
    reads: z.array(globalEventReadSchema).optional(),
    isRead: z.boolean()
});

export type GlobalEvent = z.infer<typeof globalEventSchema>;

export const getGlobalEvents = (options?: any) => {
    const { getToken } = useAuth();
    return useQuery({
        queryKey: ["globalEvents"],
        queryFn: async () => {
            const token = await getToken();
            const response = await axios.get(`${import.meta.env.VITE_API_URL}/app/global-events`, {
                headers: {
                    Authorization: `Bearer ${token}`,
                },
            });
            return z.array(globalEventSchema).parse(response.data);
        },
        refetchInterval: 30000,
        keepPreviousData: true,
        retry: false,
        ...options,
    });
};

export const useReadGlobalEvent = () => {
    const queryClient = useQueryClient();
    const { getToken } = useAuth();
    return useMutation({
        mutationFn: async (globalEventId: string) => {
            const token = await getToken();
            const response = await axios.post(
                `${import.meta.env.VITE_API_URL}/app/global-events/${globalEventId}/mark-read`,
                {}, // empty body
                {
                    headers: {
                        Authorization: `Bearer ${token}`,
                    },
                }
            );
            return globalEventReadSchema.parse(response.data);
        },
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: ["globalEvents"] });
            queryClient.invalidateQueries({ queryKey: ["mixedEvents"] });
            queryClient.invalidateQueries({ queryKey: ["user"] });
        },
    });
};